import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../ContentContext';

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { company } = useContent();
  const { requestOtp, verifyOtp, isLoggedIn } = useAuth();

  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [referral, setReferral] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [seconds, setSeconds] = useState(0);
  const otpRef = useRef(null);

  useEffect(() => { if (isLoggedIn) navigate('/account', { replace: true }); }, [isLoggedIn, navigate]);

  // A referral link (…/login?ref=CODE) pre-fills the referral field.
  useEffect(() => {
    const ref = params.get('ref');
    if (ref) setReferral(ref.trim().toUpperCase());
  }, [params]);

  // Resend countdown.
  useEffect(() => {
    if (seconds <= 0) return undefined;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const sendOtp = async () => {
    setErr('');
    if (!/^\d{10}$/.test(phone)) { setErr('Enter a valid 10-digit mobile number'); return; }
    setBusy(true);
    try {
      await requestOtp(phone);
      setStep('otp');
      setSeconds(30);
      setTimeout(() => otpRef.current?.focus(), 50);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const submitOtp = async () => {
    setErr('');
    if (!/^\d{4,6}$/.test(otp)) { setErr('Enter the OTP sent to your phone'); return; }
    setBusy(true);
    try {
      const me = await verifyOtp(phone, otp, referral.trim());
      // New users finish their profile first, like the app.
      navigate(me?.name ? '/account' : '/account?complete=1', { replace: true });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {company?.logo ? <img className="auth-logo" src={company.logo} alt={company.name} /> : null}

        {step === 'phone' ? (
          <>
            <h2>Login / Sign Up</h2>
            <p className="muted">We'll send a one-time password to your mobile number.</p>

            <div className="field" style={{ marginTop: 20 }}>
              <label>Mobile Number</label>
              <div className="phone-input">
                <span>+91</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit number"
                  inputMode="numeric"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && sendOtp()}
                />
              </div>
            </div>

            <div className="field">
              <label>Referral Code <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
              <input
                value={referral}
                onChange={(e) => setReferral(e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="Enter a friend's code"
              />
              {params.get('ref') ? (
                <span className="muted" style={{ fontSize: 12 }}>
                  <i className="fas fa-gift" style={{ color: 'var(--gold-deep)' }} /> Referral applied from your invite link
                </span>
              ) : null}
            </div>

            {err ? <p className="form-msg err">{err}</p> : null}

            <button className="consult-btn auth-btn" disabled={busy} onClick={sendOtp}>
              {busy ? 'Sending…' : 'Send OTP'}
            </button>
          </>
        ) : (
          <>
            <h2>Verify OTP</h2>
            <p className="muted">
              Enter the code sent to <strong>+91 {phone}</strong>{' '}
              <button className="link-btn inline" onClick={() => { setStep('phone'); setOtp(''); setErr(''); }}>
                Change
              </button>
            </p>

            <div className="field" style={{ marginTop: 20 }}>
              <label>OTP</label>
              <input
                ref={otpRef}
                className="otp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••••"
                inputMode="numeric"
                onKeyDown={(e) => e.key === 'Enter' && submitOtp()}
              />
            </div>

            {err ? <p className="form-msg err">{err}</p> : null}

            <button className="consult-btn auth-btn" disabled={busy} onClick={submitOtp}>
              {busy ? 'Verifying…' : 'Verify & Continue'}
            </button>

            <button className="link-btn" disabled={seconds > 0 || busy} onClick={sendOtp}>
              {seconds > 0 ? `Resend OTP in ${seconds}s` : 'Resend OTP'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
